// Timing: when the clock nudges Otto, and what it tells him. The Durable
// Object owns the clock (design 4.5): code pushes notes, Otto never calls a
// clock tool. The clock is a guide: 15 minute target, a cap Wes can set from
// 20 to 25 minutes (default 22), and near the target Otto asks whether the
// owner has time to keep going; the owner decides (Wes, Sept 28 2026).
import { z } from "zod";
import { DraftLabel, behaviorText, templateText } from "./common";

/**
 * Notes the clock sends Otto as "CLOCK NOTE, not said by the guest" (design
 * 4.1). Behavior text, so it is data. Placeholders: {first_name},
 * {business_name}, {gap_seconds}, {minutes_left}.
 */
export const ClockNotes = z.object({
  /** About 8 s after Otto's question with no answer. */
  gentle_nudge: templateText(500),
  /** About 20 s: "Still with me?" */
  still_there: templateText(500),
  /** No answer after still_there: a short close; the consult is marked unfinished. */
  silence_close: templateText(500),
  /** Near the target, when the conversation is rich: ask whether they have time to keep going. */
  extension_offer: templateText(500),
  /** The owner said yes. */
  extension_accepted: templateText(500),
  /** The owner said no, or did not answer: head for the read-back and the close. */
  extension_declined: templateText(500),
  /** Start the read-back after this answer. */
  start_readback: templateText(500),
  /** Closing beats now. */
  closing_beats: templateText(500),
  /** Close on your next turn. */
  close_next_turn: templateText(500),
  /** Close now; the current answer may finish. */
  close_now: templateText(500),
  /** The owner was away for {gap_seconds} s; check they heard the last question. */
  reconnected_after_gap: templateText(500),
  /**
   * Optional. Sent when Otto was cut off by his own echo (design 4.3): pick up
   * where he stopped. Absent: the echo is detected and counted, no note is sent.
   */
  self_interrupted: templateText(500).optional(),
  /**
   * Optional. Opens the "what I know so far" note a fresh voice session gets
   * after a rolling swap (design 4.2); code appends the ledger's facts as
   * quoted data. Absent: fresh sessions carry the verbatim transcript only.
   */
  fresh_session_ledger_intro: templateText(500).optional(),
  /**
   * Optional. Sent as a cue when the voice session dropped (or the object
   * restarted) while Otto was mid reply or owed the owner a reply, and a new
   * session took over with no gap note (review fix, Sept 28 2026: otherwise
   * the owner hears half a sentence, then nothing). Absent: the cue falls
   * back to self_interrupted, and with neither the silence steps still run.
   */
  voice_recovered: templateText(500).optional(),
});
export type ClockNotes = z.infer<typeof ClockNotes>;

export const Timing = z
  .object({
    draft_label: DraftLabel,
    target_minutes: z.number().min(5).max(30).default(15),
    /** Wes: a setting from 20 to 25 minutes, default 22. */
    cap_minutes: z.number().min(20).max(25).default(22),
    /**
     * When Otto may ask whether the owner has time to keep going (only if the
     * conversation is rich). Default 12 (was 13): the question comes BEFORE
     * the read-back starts, so the owner decides first (content engineer,
     * Sept 28 2026; the v0 seed already used 12).
     */
    extension_offer_at_minutes: z.number().min(1).max(30).default(12),
    /**
     * "Rich" for the extension offer: the owner talked at least this many
     * seconds in the last five minutes. ESTIMATE starting point; tune on real consults.
     */
    extension_rich_min_owner_seconds_last_5_min: z.number().int().min(0).max(300).default(120),
    /** Near minute 7: two sentence checkpoint (design 4.5). */
    checkpoint_at_minutes: z.number().min(0).max(30).default(7),
    /**
     * Near minute 11: the read-back is DRAFTED (design 4.5). Otto starts
     * SAYING it later, at the target minus readback_lead_seconds (13:00 by
     * default), so the draft has time to arrive first. With an extension both
     * move later by the same amount.
     */
    readback_at_minutes: z.number().min(0).max(30).default(11),
    /** "Close on your next turn" this long before "close now". */
    close_next_turn_before_cap_seconds: z.number().int().min(0).max(600).default(90),
    /**
     * The late schedule (design 4.5; moved from otto-live constants into
     * config at integration, Sept 28 2026). With the defaults the marks are
     * 13:00 start the read-back, 15:00 closing beats, 18:30 close next turn,
     * 20:00 close now, as design 4.5 has them. ESTIMATE starting points.
     */
    /** "Start the read-back after this answer" this long before the closing beats. */
    readback_lead_seconds: z.number().int().min(0).max(900).default(120),
    /** Without an extension, "close now" comes this long after the target. */
    close_now_after_target_seconds: z.number().int().min(0).max(900).default(300),
    /**
     * With an extension, the closing beats come this long before the cap (so
     * the read-back starts readback_lead_seconds earlier still).
     */
    extended_closing_beats_before_cap_seconds: z.number().int().min(0).max(900).default(300),
    /** How long an unanswered "do you have time?" holds the late notes back. */
    extension_answer_wait_seconds: z.number().int().min(0).max(300).default(90),
    /**
     * Optional. Phrases that mark Otto's turn as THE question "do you have
     * time to keep going?" (matched without case or accents). Only the
     * owner's answer to a turn that asks it (a question with one of these
     * phrases) is read as a yes or no, and the on-screen buttons show only
     * after that turn (review fix, Sept 28 2026: the answer to an unrelated
     * question was being read). Absent: the spoken answer is never read, and
     * the buttons show after the first question Otto asks once the offer
     * note reached him.
     */
    extension_question_markers: z.array(behaviorText(80)).max(40).optional(),
    /** After the cap, time for Otto's last answer and close before the hard end (design: 90 s). */
    close_grace_seconds: z.number().int().min(0).max(300).default(90),
    silence: z
      .object({
        gentle_nudge_seconds: z.number().int().min(1).max(120).default(8),
        still_there_seconds: z.number().int().min(1).max(300).default(20),
        close_after_still_there_seconds: z.number().int().min(1).max(600).default(45),
      })
      .prefault({}),
    reconnect: z
      .object({
        /** The DO keeps the voice session open this long while the browser is gone (design 4.4). */
        hold_voice_session_seconds: z.number().int().min(0).max(600).default(60),
        /** Held Otto audio replayed on return (design 4.4: up to 15 s). */
        hold_otto_audio_seconds: z.number().int().min(0).max(60).default(15),
        /** A gap longer than this sends reconnected_after_gap to Otto. */
        note_after_gap_seconds: z.number().int().min(0).max(120).default(3),
        /** After this long away the consult is marked unfinished. */
        unfinished_after_minutes: z.number().int().min(1).max(120).default(10),
      })
      .prefault({}),
    notes: ClockNotes,
  })
  .superRefine((t, ctx) => {
    if (t.target_minutes >= t.cap_minutes) {
      ctx.addIssue({ code: "custom", path: ["target_minutes"], message: "The target must be below the cap" });
    }
    if (t.extension_offer_at_minutes > t.target_minutes) {
      ctx.addIssue({ code: "custom", path: ["extension_offer_at_minutes"], message: "Offer the extension at or before the target" });
    }
    if (t.checkpoint_at_minutes >= t.readback_at_minutes) {
      ctx.addIssue({ code: "custom", path: ["checkpoint_at_minutes"], message: "The checkpoint comes before the read-back" });
    }
    if (t.readback_at_minutes >= t.cap_minutes) {
      ctx.addIssue({ code: "custom", path: ["readback_at_minutes"], message: "The read-back comes before the cap" });
    }
    const plain = lateSchedule(t, false);
    if (plain.readbackDraftAt > plain.startReadbackAt - 60) {
      ctx.addIssue({
        code: "custom",
        path: ["readback_at_minutes"],
        message: "Draft the read-back at least a minute before Otto starts saying it (target minus the read-back lead)",
      });
    }
    if (t.extension_offer_at_minutes * 60 >= plain.startReadbackAt) {
      ctx.addIssue({
        code: "custom",
        path: ["extension_offer_at_minutes"],
        message: "Offer more time before the read-back starts (target minus the read-back lead)",
      });
    }
    if (t.silence.still_there_seconds <= t.silence.gentle_nudge_seconds) {
      ctx.addIssue({ code: "custom", path: ["silence", "still_there_seconds"], message: "Still there comes after the gentle nudge" });
    }
  });
export type Timing = z.infer<typeof Timing>;

/**
 * The LATEST connected second at which the Durable Object ends any call: the
 * cap plus the close grace (an extended call). A call without an extension
 * ends earlier, at its own "close now" plus the grace (lateSchedule).
 */
export function hardEndSeconds(t: Pick<Timing, "cap_minutes" | "close_grace_seconds">): number {
  return Math.round(t.cap_minutes * 60 + t.close_grace_seconds);
}

export interface LateSchedule {
  readbackDraftAt: number;
  startReadbackAt: number;
  closingBeatsAt: number;
  closeNextTurnAt: number;
  closeNowAt: number;
  hardEndAt: number;
}

type ScheduleTiming = Pick<
  Timing,
  | "target_minutes"
  | "cap_minutes"
  | "readback_at_minutes"
  | "close_next_turn_before_cap_seconds"
  | "close_grace_seconds"
  | "readback_lead_seconds"
  | "close_now_after_target_seconds"
  | "extended_closing_beats_before_cap_seconds"
>;

/**
 * When each late clock note fires, in connected seconds (design 4.5; Wes,
 * Sept 28 2026). `extended` is true only when the owner said yes to "do you
 * have time to keep going?". Without it the consult wraps up around the
 * target; with it, around the cap. The hard end is "close now" plus the close
 * grace: 21:30 without an extension, the cap plus the grace (23:30) with one
 * (review fix, Sept 28 2026: before, a call without an extension could run
 * 3.5 minutes past its goodbye). One function, used by otto-live's clock and
 * by the golden runner, so both follow the same plan.
 */
export function lateSchedule(timing: ScheduleTiming, extended: boolean): LateSchedule {
  const target = timing.target_minutes * 60;
  const cap = timing.cap_minutes * 60;
  const closeNowAt = extended ? cap : Math.min(cap, target + timing.close_now_after_target_seconds);
  const closingBeatsAt = extended ? Math.max(target, cap - timing.extended_closing_beats_before_cap_seconds) : target;
  const closeNextTurnAt = Math.max(closingBeatsAt, closeNowAt - timing.close_next_turn_before_cap_seconds);
  const startReadbackAt = Math.max(0, closingBeatsAt - timing.readback_lead_seconds);
  // The draft keeps its distance from the read-back: an extension moves both.
  const plainStart = Math.max(0, target - timing.readback_lead_seconds);
  const readbackDraftAt = Math.round(timing.readback_at_minutes * 60 + (startReadbackAt - plainStart));
  return {
    readbackDraftAt,
    startReadbackAt,
    closingBeatsAt,
    closeNextTurnAt,
    closeNowAt,
    hardEndAt: Math.min(hardEndSeconds(timing), Math.round(closeNowAt + timing.close_grace_seconds)),
  };
}
